/**
 * StrategyInsightsPage — /strategy-insights editorial long-read.
 *
 * Authority: docs/contracts/DESIGN_IMPLEMENTATION_CONTRACT.md §8 (long-read
 *            layout), §5 (coceoVoice: active), §9 (regeneration).
 * Spec:      docs/specs/STRATEGY_INSIGHTS_PAGE_SPEC.md
 */

import React, { useCallback, useEffect, useState } from 'react';
import { DS_COLORS, DS_SPACING } from '@/config/ui-invariants';
import { LongReadLayout } from '@/components/layout/LongReadLayout';
import { StrategyIntro } from '@/components/strategy-insights/StrategyIntro';
import { PatternObservations } from '@/components/strategy-insights/PatternObservations';
import { SectorTemperature } from '@/components/strategy-insights/SectorTemperature';
import { ConvictionChangesThisWeek } from '@/components/strategy-insights/ConvictionChangesThisWeek';
import { ThesisRelevantReads } from '@/components/strategy-insights/ThesisRelevantReads';
import { getStrategyInsights, rerunStrategyInsightsSection } from '@/lib/api';
import { copy } from '@/content/copy';

export default function StrategyInsightsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const payload = await getStrategyInsights();
      setData(payload);
      setError(null);
    } catch (_err) {
      setError(copy.strategyInsights.errors.syntheseFailed);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const makeResynth = (section) => async () => {
    const result = await rerunStrategyInsightsSection(section);
    setData((prev) => ({ ...(prev || {}), [section]: result.payload }));
    return { changedKeys: result.changedKeys || [] };
  };

  const introData = data?.intro_data || {};

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: DS_SPACING.cardPadding,
        background: DS_COLORS.surface.slate,
      }}
    >
      <LongReadLayout>
        <StrategyIntro introData={introData} />

        {error && (
          <p style={{ color: DS_COLORS.text.error, margin: `0 0 ${DS_SPACING.cardGap}px 0` }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: DS_SPACING.cardGap }}>
          <PatternObservations
            patterns={data?.patterns || []}
            onResynth={makeResynth('patterns')}
          />
          <SectorTemperature
            sectors={data?.temperature || []}
            onResynth={makeResynth('temperature')}
          />
          <ConvictionChangesThisWeek
            changes={data?.conviction_changes || []}
            onResynth={makeResynth('conviction_changes')}
          />
          <ThesisRelevantReads
            reads={data?.thesis_reads || []}
            onResynth={makeResynth('thesis_reads')}
          />
        </div>
      </LongReadLayout>
    </div>
  );
}
